import { howDidYouHearAboutUs } from './constants';

export interface SourceDisplayOptions {
  patientTypeFilter: string[];
  showExistingAsDash?: boolean;
}

/**
 * Formats source information for display in appointment lists
 * @param source - The source data (string or array of strings)
 * @param options - Display options including patient type filter
 * @returns Formatted source string for display
 */
export const formatSourceDisplay = (
  source: string | string[] | undefined,
  options: SourceDisplayOptions
): string => {
  const { patientTypeFilter, showExistingAsDash = true } = options;

  // Helper function to get label from howDidYouHearAboutUs by id
  const getSourceLabel = (sourceId: string): string => {
    const sourceItem = howDidYouHearAboutUs.find(item => item.id === sourceId.trim());
    return sourceItem ? sourceItem.label.toLowerCase() : sourceId.trim().toLowerCase();
  };

  // Check if patientTypeFilter contains 'existing' and show dash
  if (patientTypeFilter.includes('existing') && showExistingAsDash) {
    return '-';
  }

  // Handle different source formats
  if (!source) {
    return '-';
  }

  if (typeof source === 'string') {
    // Single source as string
    return getSourceLabel(source);
  }

  if (Array.isArray(source)) {
    // Multiple sources as array
    const sourceLabels = source
      .filter(sourceItem => sourceItem && sourceItem.trim())
      .map(sourceItem => getSourceLabel(sourceItem));
    return sourceLabels.length > 0 ? sourceLabels.join(', ') : '-';
  }

  return '-';
};

/**
 * Formats the display text for source with proper capitalization
 * @param displaySource - The formatted source string
 * @returns Properly formatted display text
 */
export const formatSourceDisplayText = (displaySource: string): string => {
  if (displaySource === 'friends or family') {
    return 'Friends or Family';
  }
  return displaySource.charAt(0).toUpperCase() + displaySource.slice(1);
};

/**
 * Gets the CSS class for source display based on content
 * @param displaySource - The formatted source string
 * @returns CSS class string
 */
export const getSourceDisplayClass = (displaySource: string): string => {
  return `text-[#21272A] text-sm font-normal leading-normal ${displaySource !== '-' ? 'pl-2' : ''}`;
}; 